import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Subject } from 'rxjs';
import { debounceTime, exhaustMap, tap } from 'rxjs/operators';
import { useConstant } from '@wearejh/react-hooks/lib/useConstant';

import { useDeps } from 'src/hooks/useEpicDeps';

type Props = Record<string, any>;

const Context = React.createContext<{
    register();
}>({
    register: () => {
        return console.error('Not implemented - have you wrapped in context?');
    },
});

export function ReviewsContext(props: Props) {
    const evt$ = useConstant(() => new Subject());
    const { YOTPO_API_KEY, YOTPO_ENABLED } = useDeps().env;

    useEffect(() => {
        if (!YOTPO_ENABLED) return undefined;
        /**
         * Listen for events
         */
        const evts = evt$.pipe(
            debounceTime(500),
            exhaustMap(() => import('load-js/src/load-js')),
            exhaustMap((load) =>
                load.default({
                    async: true,
                    url: `https://staticw2.yotpo.com/${YOTPO_API_KEY}/widget.js`,
                }),
            ),
            tap(() => {
                const el = document.querySelector('body');
                if (el && el.getAttribute('data-yotpo-loaded') && 'yotpo' in window) {
                    const api = new (window as any).Yotpo.API((window as any).yotpo);
                    api.refreshWidgets();
                } else {
                    el && el.setAttribute('data-yotpo-loaded', 'true');
                }
            }),
        );
        const sub = evts.subscribe();
        return () => {
            sub.unsubscribe();
        };
    }, [YOTPO_API_KEY, YOTPO_ENABLED, evt$]);

    /**
     * this function is what any component can call to
     * initialise a widget
     */
    const register = useCallback(() => {
        evt$.next();
    }, [evt$]);

    /**
     * Context api
     */
    const api = useMemo(() => {
        return {
            register,
        };
    }, [register]);

    return <Context.Provider value={api}>{props.children}</Context.Provider>;
}

/**
 *
 */
export function useReviews() {
    const { register } = useContext(Context);
    useEffect(() => {
        setTimeout(() => {
            register();
        }, 0);
    }, [register]);
}
